import isNil from 'lodash/isNil'
import {
  InvitationStatus,
  UserDisplayStatus,
  UserListRow,
} from '../../api/dto/user-invitation.dto'
import { isInviteAllowedRbacRoleName } from './inviteRbacRoles'

export interface SkippedGrantRow {
  row: UserListRow
  reason: string
}

export interface GrantBuckets {
  /** No account yet: they get an invitation. */
  invite: UserListRow[]
  /** Invitation still live: it is re-issued with the selected roles. */
  resend: UserListRow[]
  /** Already active: the role applies immediately. */
  assign: UserListRow[]
  /** Nothing to do, with the reason shown to the operator. */
  skip: SkippedGrantRow[]
}

/**
 * Reasons must match what the server reports for the same conditions, so the
 * confirm step and the results read identically.
 */
export const GRANT_SKIP_REASONS = {
  deactivated: 'User is deactivated',
  noCompanies: 'No billing companies',
  alreadyActive: 'User is already active',
  alreadyGranted: 'Already has access',
} as const

export const EMPTY_GRANT_BUCKETS: GrantBuckets = {
  invite: [],
  resend: [],
  assign: [],
  skip: [],
}

/**
 * Rows the grant will actually act on, in the order the confirm step lists
 * them. Sole owner of "actionable", so the bulk bar, the modal and the request
 * cannot disagree about it.
 */
export const grantableRows = (buckets: GrantBuckets): UserListRow[] => [
  ...buckets.invite,
  ...buckets.resend,
  ...buckets.assign,
]

/**
 * Splits selected rows by what granting access would actually do to each one.
 * This is a preview built from list data the client already has, so it can be
 * stale; the server re-classifies against live Cognito and DynamoDB state and
 * its results are what count.
 */
export const buildGrantBuckets = (rows: UserListRow[]): GrantBuckets => {
  const buckets: GrantBuckets = {
    invite: [],
    resend: [],
    assign: [],
    skip: [],
  }

  for (const row of rows) {
    if (
      row.displayStatus === UserDisplayStatus.Active &&
      !isNil(row.personId) &&
      !row.isDeactivated
    ) {
      // Same test as the "Yes" pill, so the column and the button agree:
      // re-sending a role the person already holds changes nothing.
      if (
        (row.rbacRoles ?? []).some((role) =>
          isInviteAllowedRbacRoleName(role.name)
        )
      ) {
        buckets.skip.push({ row, reason: GRANT_SKIP_REASONS.alreadyGranted })
        continue
      }
      buckets.assign.push(row)
      continue
    }
    if (row.isDeactivated) {
      buckets.skip.push({ row, reason: GRANT_SKIP_REASONS.deactivated })
      continue
    }
    // Checked before the invite split because the server skips on companies
    // first, so a pending row with nothing to administer must read as a skip
    // here too rather than as a resend.
    if (row.billingClientPublicKeys.length === 0) {
      buckets.skip.push({ row, reason: GRANT_SKIP_REASONS.noCompanies })
      continue
    }
    // A live invitation is re-issued rather than left alone: that is the only
    // way to change the roles it will confer. An expired or revoked one has no
    // link to preserve, so it is a plain invite.
    if (row.invitationStatus === InvitationStatus.Pending) {
      buckets.resend.push(row)
      continue
    }
    // Active but with no personId: there is no id to write a role under, and
    // inviting is worse than useless, since issuing an activation token resets
    // a login that already works. The server refuses these for the same reason
    // once it resolves the account from Cognito.
    if (row.displayStatus === UserDisplayStatus.Active) {
      buckets.skip.push({ row, reason: GRANT_SKIP_REASONS.alreadyActive })
      continue
    }
    buckets.invite.push(row)
  }

  return buckets
}
